import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, X, Loader2 } from "lucide-react";
import { api, formatError } from "../api";
import { useAuth } from "../context/AuthContext";

/**
 * Minimal inline markdown renderer for chat bubbles.
 * Handles `**bold**` (the dominant token the LLM emits) safely without an
 * extra dependency. Every non-bold span is rendered as plain text — no HTML
 * is interpreted, so XSS-safe by default. Newlines are preserved by the
 * parent's `whitespace-pre-wrap`.
 */
function renderRichText(content) {
  if (!content) return null;
  // Split on **...** while keeping the captured groups. The regex matches
  // greedily-paired double-asterisks; unpaired ** falls through as literal.
  const parts = String(content).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(p);
    if (m) return <strong key={i} className="font-bold">{m[1]}</strong>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

export default function BuddyChat({ lab }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sid, setSid] = useState(() => localStorage.getItem(`buddy_sid_${lab.id}`) || null);
  const scrollRef = useRef(null);

  // Only students can chat
  const allowed = user?.role === "student";

  useEffect(() => {
    if (!open || !allowed) return;
    if (messages.length > 0) return;
    // Load history (latest session) once when opening
    api.get(`/lms/labs/${lab.id}/buddy/history`).then(r => {
      if (r.data.session_id) {
        setSid(r.data.session_id);
        localStorage.setItem(`buddy_sid_${lab.id}`, r.data.session_id);
      }
      setMessages(r.data.messages || []);
    }).catch(() => {});
  }, [open, allowed, lab.id, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages(m => [...m, { role: "user", content: text, created_at: new Date().toISOString() }]);
    setSending(true);
    try {
      const r = await api.post(`/lms/labs/${lab.id}/buddy`, { message: text, session_id: sid });
      setSid(r.data.session_id);
      localStorage.setItem(`buddy_sid_${lab.id}`, r.data.session_id);
      setMessages(m => [...m, { role: "assistant", content: r.data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      setMessages(m => [...m, { role: "system", content: formatError(e), created_at: new Date().toISOString() }]);
    }
    setSending(false);
  };

  if (!allowed) return null;

  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-40 px-4 py-3 rounded-full shadow-xl text-white inline-flex items-center gap-2 font-semibold text-sm transition-transform hover:scale-105"
          style={{ background: `linear-gradient(120deg, ${lab.color} 0%, ${lab.color}cc 100%)` }}
          data-testid="buddy-trigger">
          <Sparkles size={16} /> AI Study Buddy
        </button>
      )}

      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[92vw] sm:w-[400px] h-[70vh] sm:h-[560px] bg-white border border-rule rounded-sm shadow-2xl flex flex-col overflow-hidden"
          data-testid="buddy-panel">
          <div className="px-4 py-3 flex items-center justify-between text-white shrink-0"
            style={{ background: `linear-gradient(120deg, ${lab.color} 0%, ${lab.color}cc 100%)` }}>
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-widest opacity-80">{lab.code} · AI BUDDY</div>
              <div className="font-display font-bold text-base truncate">{lab.name}</div>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded-full hover:bg-white/20" data-testid="buddy-close">
              <X size={16} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-paper">
            {messages.length === 0 && (
              <div className="text-center text-sm text-muted mt-8" data-testid="buddy-empty">
                <Sparkles size={24} className="mx-auto mb-3" style={{ color: lab.color }} />
                <div className="font-display font-bold text-ink">Ask anything about {lab.code}</div>
                <div className="mt-2 px-4">I&apos;ve read the entire lab profile — focus areas, equipment, outcomes. Try:</div>
                <div className="mt-3 flex flex-col gap-2 items-center">
                  {["What will I learn in the first module?", "Which skills matter most for placements?", "Give me a 2-week study plan"].map(s => (
                    <button key={s} onClick={() => setInput(s)}
                      className="text-xs px-3 py-1.5 border border-rule rounded-sm hover:border-signal text-ink">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <div key={`${m.role}-${m.created_at}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`buddy-msg-${m.role}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-sm text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user" ? "bg-ink text-white"
                    : m.role === "system" ? "bg-red-50 border border-red-200 text-red-700"
                    : "bg-white border border-rule"
                }`}>
                  {renderRichText(m.content)}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="buddy-typing">
                <div className="bg-white border border-rule px-3 py-2 rounded-sm text-sm inline-flex items-center gap-2 text-muted">
                  <Loader2 size={14} className="animate-spin" /> Thinking…
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-rule bg-white flex items-end gap-2 shrink-0">
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask ${lab.code} buddy…`}
              rows={1}
              className="input flex-1 resize-none text-sm"
              style={{ minHeight: 40, maxHeight: 120 }}
              data-testid="buddy-input" />
            <button onClick={send} disabled={!input.trim() || sending}
              className="btn-primary !px-3 !py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="buddy-send">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
